import React, { useState, useEffect, useCallback } from 'react';
import { acsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Wifi, Power, Eye, EyeOff, RefreshCw, Router } from 'lucide-react';

export default function CustomerWifi() {
  const [ont, setOnt] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [band, setBand] = useState('2.4');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await acsAPI.getMyOnt();
      setOnt(data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load your ONT status');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changePassword = async (e) => {
    e.preventDefault();
    if (password.length < 8 || password.length > 63) return toast.error('Password must be 8-63 characters');
    if (password !== confirm) return toast.error('Passwords do not match');
    setBusy('wifi');
    try {
      const { data } = await acsAPI.changeMyWifiPassword({ password, band });
      toast.success(data.message, { duration: 6000 });
      setPassword(''); setConfirm('');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to change WiFi password'); }
    finally { setBusy(''); }
  };

  const reboot = async () => {
    if (!window.confirm('Restart your ONT (internet box)?\n\nInternet will be OFF for 2-3 minutes.')) return;
    setBusy('reboot');
    try {
      const { data } = await acsAPI.rebootMyOnt();
      toast.success(data.message, { duration: 8000 });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to reboot'); }
    finally { setBusy(''); }
  };

  const bands = ont?.wifi?.map((w) => w.band) || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>My WiFi</h2>
          <p className="text-muted text-small">Manage your internet box (ONT)</p>
        </div>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /></button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : error ? (
        <div className="card"><div className="card-body">
          <div className="empty-state"><Router size={32} /><h3>ONT not available</h3><p>{error}</p></div>
        </div></div>
      ) : (
        <>
          {/* Status card */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div className="stat-icon" style={{ background: ont.online ? '#d1fae5' : '#fee2e2', color: ont.online ? '#065f46' : '#991b1b' }}>
                <Router size={20} />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 600 }}>{ont.model} <span className={`badge badge-${ont.online ? 'success' : 'danger'}`} style={{ marginLeft: 6 }}>{ont.online ? 'Online' : 'Offline'}</span></div>
                <div className="text-muted text-small">
                  {ont.wifi.map((w) => `${w.band}GHz: ${w.ssid}`).join(' · ')}
                </div>
                <div className="text-muted text-small">{ont.connectedDevices} device(s) connected</div>
              </div>
              <button className="btn btn-danger" disabled={busy === 'reboot' || !ont.online} onClick={reboot}>
                <Power size={14} /> {busy === 'reboot' ? 'Restarting…' : 'Restart ONT'}
              </button>
            </div>
          </div>

          {/* Password change card */}
          <div className="card">
            <div className="card-header"><h3><Wifi size={14} /> Change WiFi Password</h3></div>
            <div className="card-body">
              {!ont.online && (
                <p className="text-small" style={{ color: 'var(--danger)', marginBottom: 10 }}>
                  Your ONT is offline — the change will apply automatically when it comes back online.
                </p>
              )}
              <form onSubmit={changePassword}>
                {bands.length > 1 && (
                  <div className="form-group">
                    <label className="form-label">WiFi Network</label>
                    <select className="form-control" style={{ maxWidth: 260 }} value={band} onChange={(e) => setBand(e.target.value)}>
                      {ont.wifi.map((w) => (
                        <option key={w.instance} value={w.band}>{w.ssid} ({w.band} GHz)</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <div style={{ position: 'relative' }}>
                      <input className="form-control" type={show ? 'text' : 'password'} value={password}
                        onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" minLength={8} maxLength={63} required />
                      <button type="button" onClick={() => setShow(!show)}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                        {show ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm Password</label>
                    <input className="form-control" type={show ? 'text' : 'password'} value={confirm}
                      onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" required />
                  </div>
                </div>
                <p className="text-muted text-small" style={{ marginBottom: 12 }}>
                  After changing, all your devices (phones, TVs, laptops) must reconnect with the new password.
                </p>
                <button type="submit" className="btn btn-primary" disabled={busy === 'wifi'}>
                  <Wifi size={14} /> {busy === 'wifi' ? 'Changing…' : 'Change Password'}
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
